import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Modal, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { paymentReads, products as productsApi } from '../../api/fee'
import type { Product } from '../../api/types'
import { CatalogueEditor } from '../../components/CatalogueEditor'
import { PageTitle, QueryState } from '../../components/common'
import { BillingInfoBlock, PaymentMethodInfoBlock } from '../../components/payment'
import { PayActions } from '../../components/PayActions'
import { useProject } from '../../layout/projectContext'
import { isProductPayable } from '../../lib/catalogue'
import { formatDateTime, formatPrice } from '../../lib/format'
import { hasMethod } from '../../lib/paymentHelpers'

function paidText(product: Product): string {
  const otp = product.onetimeproduct
  if (!otp || !Number(otp.status)) return ''
  return Number(product.limit) === 0
    ? `Last paid at ${formatDateTime(otp.payedAt)}`
    : `Already paid at ${formatDateTime(otp.payedAt)}`
}

/**
 * Port of `ProductListComponent` (`projects/:projectId/products[/:productId]`):
 * one-time products with the caller's own purchase record, pay (current
 * card / another card / new card saved), and — project admins — add/edit.
 */
export function ProductListPage() {
  const { projectId, isAdmin } = useProject()
  const { productId } = useParams<{ productId?: string }>()
  const navigate = useNavigate()
  const list = useApiQuery(() => productsApi.search(projectId), [projectId])
  const pm = useApiQuery(() => paymentReads.method(projectId), [projectId])
  const billing = useApiQuery(() => paymentReads.billing(projectId), [projectId])
  const deepLinked = useApiQuery(
    () =>
      productId && list.data && !list.data.some((p) => p.id === productId)
        ? productsApi.view(projectId, productId)
        : Promise.resolve(null),
    [projectId, productId, list.data],
  )
  const [editing, setEditing] = useState<Product | 'new' | null>(null)
  const base = `/projects/${encodeURIComponent(projectId)}/products`

  const columns: Column<Product>[] = [
    {
      key: 'name',
      label: 'Product',
      format: (_v, p) => (
        <>
          <Link to={`${base}/${encodeURIComponent(p.id)}`}>{p.name}</Link>
          {isAdmin && Boolean(p.hidden) && <span className="portal-muted-small">(hidden)</span>}
        </>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      align: 'right',
      numeric: true,
      format: (_v, p) => (
        <>
          {formatPrice(p.price, p.currency)}
          <span className="portal-muted-small">{Number(p.limit) === 0 ? 'payable anytime' : 'payable once'}</span>
        </>
      ),
    },
    { key: 'paid', label: 'Status', sortValue: (p) => p.onetimeproduct?.payedAt ?? '', format: (_v, p) => paidText(p) },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, p) => (
        <span className="portal-inline-actions">
          {isProductPayable(p) && (
            <Link className="portal-button-link" to={`${base}/${encodeURIComponent(p.id)}`}>
              Pay
            </Link>
          )}
          {isAdmin && (
            <button type="button" onClick={() => setEditing(p)}>
              Edit
            </button>
          )}
        </span>
      ),
    },
  ]

  const selected = productId ? (list.data?.find((p) => p.id === productId) ?? deepLinked.data ?? null) : null

  return (
    <div className="portal-card">
      <PageTitle title="Products">
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')}>
            Add product
          </button>
        )}
      </PageTitle>
      <QueryState loading={list.loading} error={list.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={list.data ?? []} rowKey={(p) => p.id} />
        </div>
        {list.data?.length === 0 && <p className="portal-text-muted">No products yet.</p>}
      </QueryState>

      {selected && (
        <Modal open title={selected.name} onClose={() => navigate(base)} className="portal-modal-wide">
          <div className="fs-modal-body">
            {!isProductPayable(selected) && (
              <p className="portal-notice portal-notice--info">
                {Number(selected.price) > 0 ? paidText(selected) || 'Not payable.' : 'This product is not payable at this moment!'}
              </p>
            )}
            <div className="portal-grid-2">
              <PaymentMethodInfoBlock projectId={projectId} pm={pm.data} />
              <div className="fs-panel">
                <h3>{isProductPayable(selected) ? 'Total to pay' : 'Price'}</h3>
                <p className="fs-amount">{formatPrice(selected.price, selected.currency)}</p>
              </div>
              <BillingInfoBlock projectId={projectId} billing={billing.data} />
            </div>
            {isProductPayable(selected) && (
              <PayActions
                projectId={projectId}
                target={{ type: 'product', products: selected.id }}
                hasPaymentMethod={hasMethod(pm.data)}
                onCharged={() => {
                  list.refetch()
                  pm.refetch()
                }}
              />
            )}
          </div>
        </Modal>
      )}

      {editing && (
        <CatalogueEditor
          kind="product"
          projectId={projectId}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.refetch()
          }}
        />
      )}
    </div>
  )
}
